import DataTable from 'Components/DataTable';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import BlockWrapper from 'Components/_NewElements/BlockWrapper';
import { memory } from 'neuro-schemas';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useMyServiceContext } from '../..';
import colors from '../../../../../config/theme/colors';
import type { Theme } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { isByInformant } from 'Utility/documentHandling';
import { useAppSelector } from 'Store/index';
import browserLanguage from 'Utility/browserLanguage';

type SectionKey = keyof Omit<IMemory, 'date' | 'coaType'>;

type QuestionsBySection = { [key in SectionKey]: Array<keyof NonNullable<IMemory[key]>> };

export const questionsBySection: QuestionsBySection = {
  memory: [
    'rememberingAgreedTasks',
    'rememberingRecentConversations',
    'repeatingQuestionsOrStories',
    'rememberingNamesOfRelatives',
    'learningNewSkills',
    'practicalSkills',
    'attitudeTowardsMemoryProblems',
  ],
  speechAndPerception: ['understandingSpeech', 'wordFinding', 'speechFluency', 'navigationAndFindingPlaces'],
  characterAndBehavior: [
    'maintainingInterestsAndHobbies',
    'slownessInDailyActivities',
    'initiative',
    'coherenceOfThought',
    'judgmentInFinancesOrProblems',
    'disinhibition',
    'selfCareAndCareForOthers',
    'visualHallucinations',
    'moodDepression',
  ],
  onsetAndDevelopment: ['problemOnsetSpeed', 'firstProblemsNoticed', 'precedingEvent', 'precedingEventDetails'],
  // PART V, PROM
  practicalCoping: [
    'needingHelpWithDecisions',
    'managingTasksOutsideHome',
    'relativesNoticedChanges',
    'relativesNoticedChangesDetails',
    'otherObservationsOrConcerns',
  ],
  // PART V, IROM
  dailyFunctioning: [
    'needingSupervision',
    'managingTasksOutsideHome',
    'managingTasksAtHome',
    'burdenOnFamilyAndFriends',
    'familyAndFriendsNeedSupport',
    'otherObservationsOrConcerns',
  ],
};

const textAreaQuestions = [
  'firstProblemsNoticed',
  'precedingEventDetails',
  'relativesNoticedChangesDetails',
  'otherObservationsOrConcerns',
];

export const Memory = (): React.JSX.Element => {
  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) ?? browserLanguage;
  const { editing, setEditingData, viewing, setViewingObj, setEditingObj, fm } = useMyServiceContext<'memory'>();
  const informants = useAppSelector(state => state.patient.informants) ?? [];

  const memoryData = editing?.data ?? viewing?.data ?? {};
  const isByInf = isByInformant(memoryData as Partial<IDocumentV2>, informants);
  const showInformantVersion = isByInf || memoryData.coaType === 'ObsRO';
  const memoryLocales = showInformantVersion ? memory.utils.informantLocalizations : memory.localizations;

  const onChangeSurveyForm = (values: TOnChangeValues, section?: keyof Omit<IMemory, 'date'>): void => {
    const newValues = Object.fromEntries(
      Object.entries(values).map(([field, value]) => [
        field,
        field !== 'date' && ![...textAreaQuestions, 'precedingEvent'].includes(field) ? Number(value) : value,
      ]),
    );

    const newData = section
      ? {
          [section]: {
            ...(memoryData[section] && typeof memoryData[section] === 'object'
              ? structuredClone(memoryData[section])
              : {}),
            ...newValues,
          },
        }
      : newValues;

    setEditingData?.({
      ...memoryData,
      ...newData,
    });
  };

  // Track page width and breakpoints
  const mdOrSmaller = useMediaQuery((theme: Theme) => theme.breakpoints.down(1401));

  return (
    <React.Fragment>
      {!viewing ? (
        <div style={{ width: '80%', color: colors.tertiaryText, marginBottom: '2rem' }}>
          <p>{memoryLocales[locale]['surveyInfo']}</p>
        </div>
      ) : null}
      <BlockWrapper
        title={viewing ? formatPartialDate(memoryData.date) : undefined}
        buttons={
          viewing
            ? [
                {
                  title: 'general.edit',
                  onClick: () => {
                    setViewingObj(null);
                    setEditingObj({ type: 'memory', data: memoryData });
                  },
                },
              ]
            : undefined
        }
      >
        {!viewing && (
          <FormRow title={'general.date'}>
            <InputHandler
              name="date"
              type="PartialDate"
              dateDefault="now"
              formData={{
                document: { date: memoryData.date },
                onChange: onChangeSurveyForm,
              }}
              editing={!!editing}
            />
          </FormRow>
        )}
        <FormRow title={'myService.parkinson.memoryInformant'}>
          <InputHandler
            name="coaType"
            type="CheckboxSingle"
            editing={!!editing}
            formData={{
              document: { coaType: memoryData.coaType === 'ObsRO' || isByInf ? true : false },
              onChange: values => {
                const field = Object.keys(values)[0];
                if (!field) return;
                const value = Object.values(values)[0];
                setEditingData?.({
                  ...memoryData,
                  [field]: value === true ? 'ObsRO' : 'PROM',
                  practicalCoping: undefined,
                  dailyFunctioning: undefined,
                });
              },
            }}
            option={'true'}
            optionFormatter={o => fm(`general.${o}`)}
            showLabel={false}
            disabled={isByInf}
          />
        </FormRow>
        {Object.keys(questionsBySection).map(s => {
          if (
            (s === 'practicalCoping' && showInformantVersion) ||
            (s === 'dailyFunctioning' && !showInformantVersion)
          ) {
            return <React.Fragment key={s} />;
          }
          return (
            <React.Fragment key={s}>
              <div>
                <p style={{ color: colors.tertiaryText, fontSize: '2.2rem', fontWeight: 600 }}>
                  {memoryLocales[locale][`${s}.title`]}
                </p>
              </div>
              <div style={{ width: mdOrSmaller ? '100%' : '60%', marginBottom: '2rem' }}>
                <DataTable
                  headers={['', '']}
                  data={{
                    rowData: questionsBySection[s as keyof typeof questionsBySection]
                      .filter(q => {
                        if (
                          q === 'precedingEventDetails' &&
                          !['illness', 'accident', 'lifeChange'].includes(
                            memoryData.onsetAndDevelopment?.precedingEvent ?? '',
                          )
                        ) {
                          return false;
                        }
                        if (
                          q === 'relativesNoticedChangesDetails' &&
                          memoryData.practicalCoping?.relativesNoticedChanges !== 2
                        ) {
                          return false;
                        }
                        return true;
                      })
                      .map(q => {
                        const sectionValue = memoryData[s as keyof typeof memoryData];
                        const value = sectionValue ? sectionValue[q as keyof typeof sectionValue] : undefined;
                        let options: number[] | string[] = [1, 2, 3];
                        if (['problemOnsetSpeed', 'relativesNoticedChanges', 'familyAndFriendsNeedSupport'].includes(q))
                          options = [1, 2];
                        if (q === 'precedingEvent') options = ['unknown', 'illness', 'accident', 'lifeChange'];
                        return [
                          <div key={q} style={{ width: mdOrSmaller ? '20rem' : '40rem' }}>
                            {memoryLocales[locale][`${s}.${q}`]}
                          </div>,
                          <div key={q} style={viewing ? { width: '30rem' } : { width: '60rem' }}>
                            <InputHandler
                              key={`${q}_input`}
                              name={q}
                              type={textAreaQuestions.includes(q) ? 'TextArea' : 'Radio'}
                              editing={!!editing}
                              formData={{
                                document: {
                                  [q]: value,
                                  precedingEventDetails: memoryData.onsetAndDevelopment?.precedingEventDetails,
                                  relativesNoticedChangesDetails:
                                    memoryData.practicalCoping?.relativesNoticedChangesDetails,
                                },
                                onChange: (values: TOnChangeValues) =>
                                  onChangeSurveyForm(values, s as keyof Omit<IMemory, 'date'>),
                              }}
                              options={options}
                              optionFormatter={(o: string | number) =>
                                memoryLocales[locale][`opts.${q}.${o}`] ?? String(o)
                              }
                              dependentFieldsList={value => {
                                if (q === 'precedingEvent' && value === 'unknown') {
                                  return ['precedingEventDetails'];
                                }
                                if (q === 'relativesNoticedChanges' && value === 1) {
                                  return ['relativesNoticedChangesDetails'];
                                }
                                return [];
                              }}
                            />
                          </div>,
                        ];
                      }),
                    rowStyle: viewing
                      ? undefined
                      : questionsBySection[s as keyof typeof questionsBySection].map(() => ({ rowAlign: 'top' })),
                  }}
                />
              </div>
            </React.Fragment>
          );
        })}
      </BlockWrapper>
      <div style={{ color: colors.tertiaryText, fontStyle: 'italic' }}>
        <p>{memoryLocales[locale]['referenceText']}</p>
      </div>
    </React.Fragment>
  );
};
